import { Container } from "@/screens/rewards/components/Container"
import { Vertical } from "@/components/standard/BFLayout"
import {
  FieldWithLabel,
  TextFieldWithLabel,
} from "@/components/forms/FieldWithLabel"
import { Masks } from "react-native-mask-input"
import { isAppleHiddenEmail, isValidEmail } from "@/utils/StringUtils"
import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import BFDropDown from "@/components/standard/BFDropDown"
import { AccordionHeader } from "@/screens/loans/components/setup/AccordionHeader"
import {
  AccordionContent,
  AccordionItem,
} from "@/components/reusables/ui/accordion"
import BFText, { Body } from "@/components/standard/BFText"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useUser } from "@/queries/user"

export interface ContactInfo {
  firstName: string
  lastName: string
  phoneNumber: string
  contactEmail: string

  borrower?: string
  borrowerName?: string
}

export interface BorrowerContactRef {
  flushContactFields: () => void
}

export const BorrowerAndContactQuestion = forwardRef<
  BorrowerContactRef,
  { onValidationChange?: (isValid: boolean) => void }
>(({ onValidationChange }, ref) => {
  const { loanApplication, update } = useLoanContext()

  const { data: userData } = useUser()
  const {
    firstName: userFirstName,
    lastName: userLastName,
    phoneNumber: userPhoneNumber,
  } = userData?.me || {}

  // We don't want to populate if an obscured Apple email is the user's login
  const displayContactEmail: string | undefined =
    userData?.me?.contactEmail ||
    (isAppleHiddenEmail(userData?.me?.email) ? "" : userData?.me?.email)

  const [firstName, setFirstName] = useState<string>(userFirstName || "")
  const [lastName, setLastName] = useState<string>(userLastName || "")
  const [phoneNumber, setPhoneNumber] = useState<string>(userPhoneNumber || "")
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>()
  const [contactEmail, setContactEmail] = useState<string>(
    displayContactEmail || ""
  )
  const [contactEmailError, setContactEmailError] = useState<
    string | undefined
  >()

  const notValidContactInfo: boolean =
    !!contactEmailError || !!phoneNumberError || !firstName || !lastName || !phoneNumber || !contactEmail

  const borrowerName = loanApplication.borrowerNames?.[0] || ""

  const notValidEntity = loanApplication.borrower === "entity" && borrowerName.length < 3

  const saveShouldBeDisabled: boolean =
    !loanApplication.borrower ||
    notValidEntity ||
    notValidContactInfo

  useEffect(() => {
    onValidationChange?.(!saveShouldBeDisabled)
  }, [saveShouldBeDisabled])

  useImperativeHandle(ref, () => ({
    flushContactFields: () => {
      const digits = phoneNumber.replace(/\D/g, "")
      update({
        firstName,
        lastName,
        contactEmail,
        phoneNumber: digits,
      })
    },
  }))

  function HeaderValue() {
    if (!loanApplication.id) {
      return ""
    }

    if (saveShouldBeDisabled) {
      return <BFText color={"error"}>{notValidEntity ? 'Enter valid Entity' : 'Missing Info'}</BFText>
    }

    if (loanApplication.borrower === "self") {
      return `${firstName} ${lastName}`
    }

    return loanApplication.borrowerNames?.[0] || "Entity"
  }

  return (
    <AccordionItem value={"borrower"}>
      <AccordionHeader
        text={"Borrower"}
        value={<HeaderValue />}
      />
      <AccordionContent>
        <Body>Verify the borrower&apos;s contact details</Body>
        <Container>
          <Vertical className={"gap-3"}>
            <FieldWithLabel
              label={"Borrowing As"}
              required={true}
              showRequiredIndicator={true}
            >
              <BFDropDown
                fullWidth={true}
                options={[
                  { label: "Self", value: "self" },
                  { label: "Entity", value: "entity" },
                ]}
                value={loanApplication.borrower || null}
                onValueChange={(value) => {
                  if (value) {
                    update({ borrower: value })
                  }
                }}
                zIndex={10}
              />
            </FieldWithLabel>

            <TextFieldWithLabel
              label={"First Name"}
              initialValue={firstName}
              required={true}
              onValueChange={(value) => {
                setFirstName(value)
              }}
            />

            <TextFieldWithLabel
              label={"Last Name"}
              initialValue={lastName}
              required={true}
              onValueChange={(value) => {
                setLastName(value)
              }}
            />

            <TextFieldWithLabel
              label={"Phone Number"}
              initialValue={phoneNumber}
              required={true}
              validator={(value) =>
                value.length !== 10 ? "Invalid phone number" : undefined
              }
              onValueChange={(value, error) => {
                setPhoneNumber(value)
                setPhoneNumberError(error)
              }}
              mask={Masks.USA_PHONE}
              textFieldProps={{
                placeholder: "(___) ___-____",
                keyboardType: "numeric",
              }}
            />

            <TextFieldWithLabel
              label={"Email"}
              initialValue={contactEmail}
              required={true}
              validator={(value) =>
                isValidEmail(value) ? undefined : "Not a valid email address"
              }
              onValueChange={(value, error) => {
                setContactEmail(value)
                setContactEmailError(error)
              }}
            />

            {loanApplication.borrower === "entity" && (
              <TextFieldWithLabel
                label={"Entity Name"}
                initialValue={loanApplication.borrowerNames?.[0] || ""}
                required={true}
                onValueChange={(value) => {
                  update({ borrowerNames: [value] })
                }}
              />
            )}
          </Vertical>
        </Container>
      </AccordionContent>
    </AccordionItem>
  )
})
